import React, { useMemo } from 'react';
import { InteractiveElementType } from '../messageBubbleBuilder';
import AuthBubble from './AuthBubble';

const ChatMessage = ({ message, sessionInfo, onAuthSignIn, interactiveHandlers = {} }) => {
  const authProviders = useMemo(() => {
    if (!Array.isArray(message?.interactiveElements)) return [];

    const providers = message.interactiveElements
      .filter((element) => element?.type === InteractiveElementType.SIGN_IN)
      .filter((element) => {
        if (!element.roleRequired) return true;
        return sessionInfo?.role === element.roleRequired;
      })
      .map((element) => element.provider)
      .filter(Boolean);

    return Array.from(new Set(providers));
  }, [message?.interactiveElements, sessionInfo?.role]);

  const handleAuthSignIn = async (provider) => {
    const handler = onAuthSignIn || interactiveHandlers[provider];
    if (handler) {
      await handler(provider);
    }
  };

  // Handle system messages (Apple sign-in, etc.)
  if (message.sender === 'system') {
    if (authProviders.length > 0) {
      return (
        <div className="flex justify-start">
          <AuthBubble
            promptText={message.text}
            providers={authProviders}
            onSignIn={handleAuthSignIn}
            timestamp={message.timestamp}
          />
        </div>
      );
    }

    return (
      <div className="flex justify-center">
        <div
          className={
            'max-w-xs lg:max-w-md px-4 py-2 rounded-2xl bg-gray-800 bg-opacity-70 ' +
            'border border-gray-600 text-gray-200 shadow-md'
          }
        >
          <div className="flex items-center space-x-2">
            <span className="text-sm">🍎</span>
            <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
          </div>
          <p className="text-xs mt-1 opacity-70 text-center">
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
    );
  }

  // Handle bot messages (regular responses)
  if (message.sender === 'bot') {
    if (authProviders.length > 0) {
      return (
        <div className="flex justify-start">
          <AuthBubble
            promptText={message.text}
            providers={authProviders}
            onSignIn={handleAuthSignIn}
            timestamp={message.timestamp}
          />
        </div>
      );
    }

    return (
      <div className="flex justify-start">
        <div
          className={
            'max-w-xs lg:max-w-md px-4 py-2 rounded-2xl bg-gray-700 bg-opacity-70 ' +
            'text-gray-200 shadow-md'
          }
        >
          <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
          <p className="text-xs mt-1 opacity-70">
            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </p>
        </div>
      </div>
    );
  }

  // Handle user messages
  return (
    <div className="flex justify-end">
      <div
        className={
          'max-w-xs lg:max-w-md px-4 py-2 rounded-2xl bg-blue-600 bg-opacity-70 ' +
          'text-white shadow-md'
        }
      >
        <p className="text-sm whitespace-pre-wrap break-words">{message.text}</p>
        <p className="text-xs mt-1 opacity-70">
          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
};

export default ChatMessage;
